import React, { useContext, useEffect, useState } from "react"
import { Button, Form } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { Formik } from "formik"
import _ from "lodash"

import { useModal } from "@/helpers/useModal"
import ConfirmationModal from "@/onboarding/features/inventory_allocation/components/ConfirmationModal"

import BaseComponent, { ComponentType } from "@/onboarding/features/product/models/Component"
import {
  CRUISE_LINE_FIELD_PLACEHOLDER,
  ReservedFields,
} from "@/onboarding/features/product/models/Product"
import {
  appendFieldListItem,
  removeFieldListItem,
  updateComponent,
  updateFieldListItem,
} from "@/onboarding/features/product/Slice"
import SubmittingSpinner from "@/onboarding/features/product/components/shared/SubmittingSpinner"
import { SubmittingContext } from "@/onboarding/features/product/helpers"
import {
  getAuthDescriptor,
  getComponentTender,
  getFeatures,
  getFormsEditable,
  getProduct,
  getProductId,
  isOperatorTourIdea,
} from "@/onboarding/features/product/selectors"
import SubmitButton from "@/onboarding/features/product/components/shared/buttons/SubmitButton"
import SubmitAndApplyButton from "@/onboarding/features/product/components/shared/buttons/SubmitAndApplyButton"
import ButtonLink from "@/components/ButtonLink"
import routes from "@/routes"
import SingleSelect from "@/components/input/SingleSelect"
import TrackFormDirty from "@/onboarding/features/product/forms/TrackFormDirty"

import SimpleComponentView from "./Simple"
import TransportComponentView from "./Transport"
import VenueComponentView from "./Venue"
import RefreshmentComponentView from "./Refreshment"
import RestaurantComponentView from "./Restaurant"
import EntertainmentComponentView from "./Entertainment"
import GuideComponentView from "./Guide"
import AttachmentComponentView from "./Attachment"

export const getView = (component: BaseComponent) => {
  switch (component.kind) {
    case ComponentType.SIMPLE:
      return SimpleComponentView
    case ComponentType.TRANSPORT:
      return TransportComponentView
    case ComponentType.VENUE:
      return VenueComponentView
    case ComponentType.REFRESHMENT:
      return RefreshmentComponentView
    case ComponentType.RESTAURANT:
      return RestaurantComponentView
    case ComponentType.ENTERTAINMENT:
      return EntertainmentComponentView
    case ComponentType.GUIDE:
      return GuideComponentView
    case ComponentType.ATTACHMENT:
      return AttachmentComponentView
    default:
      throw new Error(`Unknown component kind: ${component.kind}`)
  }
}

const ComponentBidStatus = ({ bid }): JSX.Element => {
  switch (bid.status) {
    case "draft":
      return <span className="text-warning">Draft</span>
    case "submitted":
      return <span className="text-info">Bid Submitted</span>
    case "rejected":
      return <span className="text-danger">Bid Rejected</span>
    case "accepted":
      return <span className="text-success">Bid Accepted</span>
    default:
      return null
  }
}

// A pending row is one "Add …" has started but not yet saved: it lives only in
// the section's state until its first Save appends it to the document, and its
// Cancel discards it.
const EditWrapper = ({
  fieldName,
  index,
  component,
  editable = true,
  originalValues = false,
  pending = false,
  onSaved = undefined,
  onDiscard = undefined,
}: {
  fieldName: ReservedFields
  index: number
  component: BaseComponent
  editable: boolean
  originalValues?: boolean
  pending?: boolean
  onSaved?: () => void
  onDiscard?: () => void
}): JSX.Element => {
  const dispatch = useDispatch()

  const [selectedBid, setSelectedBid] = useState(null)

  const productId = useSelector(getProductId)
  const formsEditable = useSelector(getFormsEditable)
  const [submitting, setSubmitting] = useContext(SubmittingContext)
  const features = useSelector(getFeatures)
  const initialEditingState =
    pending ||
    (features.general.product.componentsEditable && formsEditable) ||
    selectedBid?.canEdit
  const [editing, setEditing] = useState(initialEditingState)
  const componentTender = useSelector(getComponentTender(component.id))
  const seasonId = useSelector(getProduct).season.id

  const selectedComponent = selectedBid?.component || component
  const isTourOperatorIdea = useSelector(isOperatorTourIdea)
  const { isTourUi } = useSelector(getAuthDescriptor)

  const referencedValueEmptyMessage = isTourOperatorIdea ? CRUISE_LINE_FIELD_PLACEHOLDER : ""

  const showOperator = !isTourUi && (selectedBid ? !selectedBid.canEdit : true)

  useEffect(() => {
    if (editing !== initialEditingState) {
      setEditing(initialEditingState)
    }
  }, [initialEditingState])

  useEffect(() => {
    if (isTourUi && componentTender && !_.isEmpty(componentTender.bids)) {
      setSelectedBid(_.head(componentTender.bids))
    }
  }, [isTourUi, componentTender])

  const View = getView(component)

  const toggleConfirmationModal = useModal((props) => (
    <ConfirmationModal
      onConfirmationCallback={() => {
        setSubmitting(true)
        dispatch(removeFieldListItem({ fieldName, index }))
      }}
      buttonText="Remove"
      confirmationPrompt="Are you sure you want to remove this component?"
      header="Confirm Removal"
      {...props}
    />
  ))

  const onSubmit = (values, { resetForm }) => {
    setSubmitting(true)

    // TODO(product): Let's generalize all component updates to use the same action
    if (pending) {
      dispatch(appendFieldListItem({ fieldName, value: values })).then((action) => {
        if (action.meta.requestStatus === "fulfilled") onSaved?.()
      })
    } else if (selectedBid?.canEdit) {
      dispatch(
        updateComponent({
          componentId: component.id,
          fieldName,
          value: values,
          componentBidId: selectedBid.id,
        })
      )
    } else {
      dispatch(updateFieldListItem({ fieldName, index, value: values }))
    }

    resetForm({ values })
  }

  return (
    <Formik initialValues={selectedComponent} onSubmit={onSubmit}>
      {({ handleSubmit, dirty, resetForm }) => {
        // Cancel throws away what has not been saved: a pending row entirely,
        // otherwise the unsaved edits, back to the saved values. A row whose
        // edit mode is opt-in (Edit button) also leaves edit mode.
        const cancel = () => {
          if (pending) {
            onDiscard?.()
            return
          }

          resetForm()
          if (!initialEditingState) setEditing(false)
        }

        // Remove never sits beside unsaved work: not on a pending row, and not
        // while a row has edits Cancel would discard.
        const cancellable = editing && (pending || dirty || !initialEditingState)
        const removable = editable && !pending && !cancellable

        // A row holding work that is not saved yet is marked, so it stands out
        // from the saved rows around it until Save or Cancel.
        const unsaved = pending || (editing && dirty)

        return (
          <div
            className={`d-flex gap-2 mb-2${unsaved ? " border-start border-3 border-warning ps-2" : ""}`}
          >
            <div style={{ width: "20px" }}>{`${index + 1}.`}</div>
            <div className="flex-grow-1">
              <Form className="d-flex flex-column gap-2" onSubmit={handleSubmit}>
                <TrackFormDirty formId={component.id} />
                <View
                  component={selectedComponent}
                  previousValues={originalValues}
                  editing={editing}
                  hideRates={false}
                  showOperator={showOperator}
                  tender={componentTender}
                  referencedValueMessage={referencedValueEmptyMessage}
                />
                {unsaved && (
                  <div className="d-flex gap-2 align-items-center">
                    {dirty && (
                      <>
                        <SubmitButton />
                        {!pending && <SubmitAndApplyButton />}
                        <SubmittingSpinner />
                      </>
                    )}
                    <small className="text-muted fst-italic">
                      {pending ? "New, not saved yet" : "Unsaved changes"}
                    </small>
                  </div>
                )}
              </Form>
            </div>
            <div className="d-flex flex-column gap-2">
              {cancellable && (
                <div>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    disabled={submitting}
                    className="text-start"
                    onClick={cancel}
                  >
                    <div key="cancel">
                      <i className="fas fa-times me-2" />
                      Cancel
                    </div>
                  </Button>
                </div>
              )}
              {!editing &&
                ((editable && features.general.product.componentsEditable) ||
                  selectedBid?.canSubmit) && (
                  <div>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={submitting}
                      className="text-start"
                      onClick={() => setEditing(true)}
                    >
                      <div key="edit">
                        <i className="fas fa-edit me-2" />
                        Edit
                      </div>
                    </Button>
                  </div>
                )}
              {componentTender?.canEnableBidding && (
                <div>
                  <ButtonLink
                    label="Enable Bidding"
                    disabled={submitting}
                    button={{ size: "sm", variant: "outline-primary" }}
                    modal
                    icon="fas fa-gavel"
                    href={routes.new_onboarding_bidding_enable_product_component_bidding_path({
                      product_id: productId,
                      product_component_id: component.id,
                    })}
                  />
                </div>
              )}
              {componentTender?.canOpenBidding && (
                <div>
                  <ButtonLink
                    label="Open Bidding"
                    disabled={submitting}
                    button={{ size: "sm", variant: "outline-success" }}
                    modal
                    icon="fas fa-gavel"
                    href={routes.new_onboarding_bidding_open_current_tender_bidding_round_path({
                      tender_id: componentTender.tenderId,
                    })}
                  />
                </div>
              )}
              {componentTender?.canCloseBidding && (
                <div>
                  <ButtonLink
                    label="Close Bidding"
                    disabled={submitting}
                    button={{ size: "sm", variant: "outline-danger" }}
                    modal
                    icon="fas fa-gavel"
                    href={routes.new_onboarding_bidding_close_current_tender_bidding_round_path({
                      tender_id: componentTender.tenderId,
                    })}
                  />
                </div>
              )}
              {componentTender?.canInviteBidders && (
                <div>
                  <ButtonLink
                    label="Invite Bidders"
                    disabled={submitting}
                    button={{ size: "sm", variant: "outline-primary" }}
                    modal
                    icon="fas fa-envelope"
                    href={routes.new_onboarding_bidding_invite_tender_bidder_path({
                      season_id: seasonId,
                      tender_id: componentTender.tenderId,
                    })}
                  />
                </div>
              )}
              {componentTender?.canRevokeInvites && (
                <div>
                  <ButtonLink
                    label="Revoke Invite"
                    disabled={submitting}
                    button={{ size: "sm", variant: "outline-danger" }}
                    modal
                    icon="fas fa-user-times"
                    params={{
                      bid_ids: selectedBid ? [selectedBid.id] : [],
                    }}
                    href={routes.new_onboarding_bidding_revoke_tender_bid_invite_path({
                      tender_id: componentTender.tenderId,
                    })}
                  />
                </div>
              )}
              {selectedBid?.canSubmit && (
                <div>
                  <ButtonLink
                    label="Submit Bid"
                    disabled={submitting}
                    button={{ size: "sm", variant: "outline-success" }}
                    modal
                    icon="fas fa-file-alt"
                    href={routes.new_onboarding_bidding_submit_tender_bid_path({
                      tender_id: componentTender.tenderId,
                      bid_id: selectedBid.id,
                    })}
                  />
                </div>
              )}
              {selectedBid?.canAccept && (
                <div>
                  <ButtonLink
                    label="Accept Bid"
                    disabled={submitting}
                    button={{ size: "sm", variant: "outline-success" }}
                    modal
                    icon="fas fa-check"
                    href={routes.new_onboarding_bidding_accept_tender_bid_path({
                      tender_id: componentTender.tenderId,
                      bid_id: selectedBid.id,
                    })}
                  />
                </div>
              )}
              {selectedBid?.canReject && (
                <div>
                  <ButtonLink
                    label="Reject Bid"
                    disabled={submitting}
                    button={{ size: "sm", variant: "outline-danger" }}
                    modal
                    icon="fas fa-times"
                    href={routes.new_onboarding_bidding_reject_tender_bid_path({
                      tender_id: componentTender.tenderId,
                      bid_id: selectedBid.id,
                    })}
                  />
                </div>
              )}
              {selectedBid?.canPend && (
                <div>
                  <ButtonLink
                    label="Request Resubmission"
                    disabled={submitting}
                    button={{ size: "sm", variant: "outline-warning" }}
                    modal
                    icon="fas fa-hourglass-half"
                    href={routes.new_onboarding_bidding_pend_tender_bid_path({
                      tender_id: componentTender.tenderId,
                      bid_id: selectedBid.id,
                    })}
                  />
                </div>
              )}
              {removable && (
                <div>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="text-start"
                    disabled={submitting}
                    onClick={toggleConfirmationModal}
                  >
                    <i className="fas fa-trash me-2" />
                    Remove
                  </Button>
                </div>
              )}
              {componentTender?.bids && (
                <SingleSelect
                  name="selected_bid"
                  emptyMessage="No bids submitted yet"
                  options={[{ value: "original", label: "Original Values" }].concat(
                    componentTender.bids.map((bid) => ({
                      value: bid.id,
                      label: bid.bidder.name,
                    }))
                  )}
                  onChange={(e) =>
                    setSelectedBid(
                      componentTender.bids.find((bid) => bid.id === e.target.value) || null
                    )
                  }
                  value={selectedBid?.id || "original"}
                  label={isTourUi ? "Select Bid" : "Submitted Bids"}
                />
              )}
              {selectedBid && <ComponentBidStatus bid={selectedBid} />}
            </div>
          </div>
        )
      }}
    </Formik>
  )
}

export default EditWrapper
